import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOpportunityNameFilters } from "@/helpers/apis";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { GET_OPPORTUNITY_NAME_FILTERS } from "../constants";

interface OpportunityNameOption {
  label: string;
  value: string;
}

interface UseOpportunityNamesQueryOptions {
  enabled?: boolean;
}

export const useOpportunityNamesQuery = (options: UseOpportunityNamesQueryOptions = {}) => {
  const { enabled = true } = options;
  const { searchParams } = useFiltersBySearchParams();
  // Scope the opportunity_name typeahead to the active Workspace chip
  // selection — same contract the other three typeaheads honour. Without
  // this, picking a Workspace narrows the list but leaves the
  // opportunity_name suggestions tenant-global, so the user can select a
  // name that immediately yields zero results inside the scoped list.
  const workspaceIds = useMemo(() => searchParams.getAll("workspaceId"), [searchParams]);
  const queryParams = useMemo(
    () => (workspaceIds.length > 0 ? { workspace_id: workspaceIds } : undefined),
    [workspaceIds]
  );
  const query = useQuery({
    queryKey: [GET_OPPORTUNITY_NAME_FILTERS, workspaceIds],
    queryFn: () => getOpportunityNameFilters(queryParams),
    enabled,
  });

  // Memoise the label-rename transform on the query data so the
  // returned array identity is stable across renders while the data
  // hasn't changed — without this, every consumer that includes
  // ``opportunityNames`` in a ``useMemo`` dep array (e.g. table headers)
  // would re-compute on every render.
  const opportunityNames = useMemo(
    () =>
      query.data?.map((opportunity: OpportunityNameOption) => {
        if (opportunity.label === "frequent_short_warehouse_activity") {
          return {
            label: "short_warehouse_activity",
            value: "frequent_short_warehouse_activity",
          };
        }
        return opportunity;
      }),
    [query.data]
  );

  return {
    ...query,
    opportunityNames,
  };
};
